import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { fetchUserProfile } from '../util/api/user';
import axios from 'axios';

/**
 * Custom hook for managing user profile data
 * Extracted from profile.js for reuse in mobile and web components
 */
export const useProfileData = (userHandle, authenticatedUserHandle, authenticatedUserId, editProfile = false) => {
  const [data, setData] = useState({});
  const [userData, setUserData] = useState(null);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [targetUserId, setTargetUserId] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to get user data including premium level
  const getUserData = useCallback(async (handle) => {
    try {
      const response = await axios.get(`/user/${handle}`);
      return response.data.user || null;
    } catch (error) {
      console.error(`Error fetching data for user ${handle}:`, error);
      return null;
    }
  }, []);

  // Function to check if current user is following the profile user
  const checkIfFollowing = useCallback(async (targetUserHandle) => {
    try {
      if (!authenticatedUserId || !targetUserHandle || targetUserHandle === authenticatedUserHandle) {
        return false;
      }

      const currentUserRef = doc(firestore, "users", authenticatedUserHandle);
      const currentUserSnap = await getDoc(currentUserRef);

      if (!currentUserSnap.exists()) {
        // Fallback: try to find the user document by querying for the handle
        const currentUserQuery = query(
          collection(firestore, "users"),
          where("handle", "==", authenticatedUserHandle)
        );

        const currentUserSnapshot = await getDocs(currentUserQuery);

        if (currentUserSnapshot.empty) {
          console.error("Could not find current user document");
          return false;
        }

        const userData = currentUserSnapshot.docs[0].data();
        const following = userData.following || [];

        const normalizedTargetHandle = targetUserHandle.toLowerCase();
        return following.some(handle => handle.toLowerCase() === normalizedTargetHandle);
      }

      const userData = currentUserSnap.data();
      const following = userData.following || [];

      const normalizedTargetHandle = targetUserHandle.toLowerCase();
      return following.some(handle => handle.toLowerCase() === normalizedTargetHandle);
    } catch (error) {
      console.error("Error checking follow status:", error);
      return false;
    }
  }, [authenticatedUserHandle, authenticatedUserId]);

  // Main function to fetch user profile data
  const fetchUserData = useCallback(async () => {
    if (!userHandle) return;

    try {
      setLoading(true);
      setError(null);

      const result = await fetchUserProfile(userHandle);
      setData(result.user);

      if (!userHandle) {
        setLoading(false);
        return;
      }

      // If user exists in the database
      if (result.user && typeof result.user !== "string") {
        // Fetch the user's document to get followers and following arrays
        const userQuery = query(
          collection(firestore, "users"),
          where("handle", "==", userHandle)
        );

        const userSnapshot = await getDocs(userQuery);

        if (!userSnapshot.empty) {
          const userDoc = userSnapshot.docs[0];
          const userData = userDoc.data();

          // Store the target user ID for later use
          setTargetUserId(userDoc.id);

          // Get followers and following counts
          const followers = userData.followers || [];
          const following = userData.following || [];

          setFollowersCount(followers.length);
          setFollowingCount(following.length);

          // Store the actual lists of followers and following
          setFollowersList(followers);
          setFollowingList(following);

          // Check if current user is following this profile
          if (!editProfile && authenticatedUserHandle && authenticatedUserId) {
            const followStatus = await checkIfFollowing(userHandle);
            setIsFollowing(followStatus);
          }
        } else {
          // Try a case-insensitive search
          const allUsersSnapshot = await getDocs(collection(firestore, "users"));
          const matchingUser = allUsersSnapshot.docs.find(doc =>
            doc.data().handle &&
            doc.data().handle.toLowerCase() === userHandle.toLowerCase()
          );

          if (matchingUser) {
            const userData = matchingUser.data();
            setTargetUserId(matchingUser.id);

            // Get followers and following counts
            const followers = userData.followers || [];
            const following = userData.following || [];

            setFollowersCount(followers.length);
            setFollowingCount(following.length);

            // Store the actual lists of followers and following
            setFollowersList(followers);
            setFollowingList(following);

            // Check if current user is following this profile
            if (!editProfile && authenticatedUserHandle && authenticatedUserId) {
              const followStatus = await checkIfFollowing(userHandle);
              setIsFollowing(followStatus);
            }
          } else {
            console.error("User not found even with case-insensitive search:", userHandle);
          }
        }
      }

      // Fetch user data with premium info
      if (userHandle) {
        const data = await getUserData(userHandle);
        if (data) {
          setUserData(data);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching user:", error);
      setError(error.message);
      setLoading(false);
    }
  }, [userHandle, authenticatedUserHandle, authenticatedUserId, editProfile, checkIfFollowing, getUserData]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (userHandle) {
      fetchUserData();
    }
  }, [fetchUserData]);

  // Helper functions
  const refreshProfileData = useCallback(async () => {
    try {
      await fetchUserData();
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  }, [fetchUserData]);

  return {
    // Data
    data,
    userData,
    followersCount,
    followingCount,
    followersList,
    followingList,
    targetUserId,
    isFollowing,
    loading,
    error,

    // Functions
    refreshProfileData,
    checkIfFollowing,
    getUserData,

    // State setters for external updates
    setFollowersCount,
    setFollowingCount,
    setFollowersList,
    setFollowingList,
    setIsFollowing
  };
};